import axios from 'axios';
import { config, validateFile } from '../config/config';

class CVParserService {
  async parseCV(file, email = null, position = null, positionRecordId = null, onProgress = null) {
    // Declare progressInterval at function scope so it's accessible in catch block
    let progressInterval;

    try {
      // File size validation (10MB max)
      const MAX_SIZE = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > MAX_SIZE) {
        return {
          success: false,
          error: `File size exceeds 10MB limit. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB. Please upload a smaller file.`
        };
      }

      const formData = new FormData();
      formData.append('cv', file);
      formData.append('filename', file.name);
      formData.append('filesize', file.size);

      // Include email, position name, and position record ID for immediate Airtable record creation
      if (email) {
        formData.append('email', email);
      }
      if (position) {
        formData.append('position', position);
      }
      if (positionRecordId) {
        formData.append('position_record_id', positionRecordId);
      }

      // Progressive progress simulation with smooth animation
      let currentProgress = 10;

      if (onProgress) {
        onProgress(currentProgress, 'Uploading CV...');

        // Smooth progress increment with smaller steps for fluid animation
        progressInterval = setInterval(() => {
          if (currentProgress < 90) {
            // Gradual slowdown as we approach 90% to wait for actual response
            const increment = currentProgress < 50 ? 1.5 : currentProgress < 75 ? 1 : 0.5;
            currentProgress += increment;

            if (currentProgress > 90) currentProgress = 90;

            const messages = [
              'Uploading CV...',
              'Extracting text from document...',
              'Analyzing your experience...',
              'Identifying skills and qualifications...',
              'Processing employment history...',
              'Finalizing extraction...'
            ];
            const messageIndex = Math.floor((currentProgress / 90) * (messages.length - 1));
            onProgress(Math.floor(currentProgress), messages[messageIndex]);
          }
        }, 200); // Smooth 200ms updates for fluid animation
      }

      const response = await axios.post(config.api.cvParserWebhook, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: config.api.timeout
        // Removed onUploadProgress to prevent conflicts with smooth progress interval
      });

      // Clear the progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }

      // Smooth transition to 100%
      if (onProgress) {
        onProgress(92, 'Processing results...');
        await new Promise(resolve => setTimeout(resolve, 200));
        onProgress(96, 'Finalizing...');
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      if ((response.data.status === 'completed' || response.data.status === 'success') && response.data.parsed_data) {
        if (onProgress) {
          onProgress(100, 'Complete!');
        }

        return {
          success: true,
          data: response.data.parsed_data,
          recordId: response.data.record_id // Airtable record ID for future updates
        };
      }

      throw new Error('Invalid response format');
    } catch (error) {
      // Clear progress interval on error
      if (progressInterval) {
        clearInterval(progressInterval);
      }

      console.error('CV processing error:', error);

      // Comprehensive error handling - never block the candidate
      let userFriendlyMessage = 'Could not parse CV automatically. You can still fill the form manually.';
      let errorCategory = 'unknown';

      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        userFriendlyMessage = 'CV parsing is taking longer than expected. Please fill the form manually.';
        errorCategory = 'timeout';
      } else if (error.code === 'ERR_NETWORK' || error.code === 'ENETUNREACH') {
        userFriendlyMessage = 'Network error. Please check your connection and try again.';
        errorCategory = 'network';
      } else if (error.response?.status === 413) {
        userFriendlyMessage = 'File is too large. Please upload a file smaller than 10MB.';
        errorCategory = 'file_size';
      } else if (error.response?.status === 415) {
        userFriendlyMessage = 'Invalid file type. Please upload a PDF, DOC, or DOCX file.';
        errorCategory = 'file_type';
      } else if (error.response?.status === 429) {
        userFriendlyMessage = 'Too many requests. Please wait a moment and try again.';
        errorCategory = 'rate_limit';
      } else if (error.response?.status >= 500) {
        userFriendlyMessage = 'Server error. Please try again or fill the form manually.';
        errorCategory = 'server_error';
      } else if (error.response?.status === 400) {
        userFriendlyMessage = 'Invalid request. Please ensure all required fields are filled.';
        errorCategory = 'validation';
      } else if (!navigator.onLine) {
        userFriendlyMessage = 'No internet connection. Please check your connection and try again.';
        errorCategory = 'offline';
      } else if (error.message.includes('JSON')) {
        userFriendlyMessage = 'Error processing CV response. Please fill the form manually.';
        errorCategory = 'parse_error';
      }

      // Log error details for debugging (but don't show to user)
      console.error('Error details:', {
        category: errorCategory,
        status: error.response?.status,
        code: error.code,
        message: error.message,
        stack: error.stack?.substring(0, 200)
      });

      return {
        success: false,
        error: userFriendlyMessage,
        errorCategory: errorCategory,
        originalError: error.message,
        canRetry: ['timeout', 'network', 'rate_limit', 'server_error'].includes(errorCategory)
      };
    }
  }

  validateFile(file) {
    // Enhanced validation with file size check
    const validation = validateFile(file, 'cv');

    if (!validation.valid) {
      return validation;
    }

    // Additional 10MB size check
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      return {
        valid: false,
        error: `File size must be less than 10MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`
      };
    }

    return { valid: true };
  }
}

export default new CVParserService();